import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import * as authApi from '../api/auth';
import clsx from 'clsx';

type SearchMode = 'email' | 'username';

interface Notification {
  type: 'success' | 'error' | 'info';
  message: string;
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [searchMode, setSearchMode] = useState<SearchMode>('email');
  const [searchValue, setSearchValue] = useState('');
  const [notification, setNotification] = useState<Notification | null>(null);
  const [foundInfo, setFoundInfo] = useState<{ username?: string; email?: string; fullEmail?: string; userId?: number } | null>(null);
  const [resetUrl, setResetUrl] = useState<string | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotification(null);
    setFoundInfo(null);

    if (!searchValue.trim()) {
      setNotification({
        type: 'error',
        message: searchMode === 'email' ? '请输入邮箱地址' : '请输入用户名'
      });
      return;
    }

    // Email validation if searching by email
    if (searchMode === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(searchValue)) {
        setNotification({
          type: 'error',
          message: '请输入有效的邮箱地址'
        });
        return;
      }
    }

    try {
      const result = await authApi.findAccount(searchMode, searchValue);

      if (result.found) {
        setFoundInfo({
          username: result.username,
          email: result.email,
          fullEmail: result.fullEmail,
          userId: result.userId
        });
        setNotification({
          type: 'success',
          message: '找到账号信息'
        });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || '查询失败，请稍后重试';
      setNotification({
        type: 'error',
        message: errorMessage
      });
    }
  };

  const handleSendResetEmail = async () => {
    if (!foundInfo?.fullEmail) {
      setNotification({
        type: 'error',
        message: '邮箱信息缺失'
      });
      return;
    }

    try {
      const result = await authApi.requestPasswordReset(foundInfo.fullEmail);

      if (result.success) {
        setResetUrl(result.resetUrl);
        setNotification({
          type: 'success',
          message: result.message || '密码重置链接已生成'
        });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || '发送失败，请稍后重试';
      setNotification({
        type: 'error',
        message: errorMessage
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      <div className="w-full bg-white border-b border-gray-100">
        <div className="max-w-lg mx-auto px-4 py-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/login')}
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">找回密码</h1>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pt-8 pb-12">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                通过邮箱或用户名查找账号信息，或请求重置密码
              </p>

              <div className="flex space-x-2 mb-4">
                <button
                  onClick={() => {
                    setSearchMode('email');
                    setSearchValue('');
                    setNotification(null);
                    setFoundInfo(null);
                  }}
                  className={clsx(
                    'flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors',
                    searchMode === 'email'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  通过邮箱
                </button>
                <button
                  onClick={() => {
                    setSearchMode('username');
                    setSearchValue('');
                    setNotification(null);
                    setFoundInfo(null);
                  }}
                  className={clsx(
                    'flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors',
                    searchMode === 'username'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  通过用户名
                </button>
              </div>
            </div>

            <form onSubmit={handleSearch} className="space-y-4">
              <div>
                <label htmlFor="searchValue" className="block text-sm font-medium text-gray-700 mb-2">
                  {searchMode === 'email' ? '邮箱地址' : '用户名'}
                </label>
                <input
                  id="searchValue"
                  type={searchMode === 'email' ? 'email' : 'text'}
                  required
                  className="block w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:ring-red-500 focus:border-red-500"
                  placeholder={searchMode === 'email' ? '请输入注册时使用的邮箱' : '请输入用户名'}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                查询账号信息
              </button>
            </form>

            {notification && (
              <div
                className={clsx(
                  'mt-4 text-sm text-center py-3 px-4 rounded-lg',
                  notification.type === 'success' && 'bg-green-50 text-green-600',
                  notification.type === 'error' && 'bg-red-50 text-red-600',
                  notification.type === 'info' && 'bg-blue-50 text-blue-600'
                )}
              >
                {notification.message}
              </div>
            )}

            {foundInfo && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-2">找到的账号信息：</h3>
                {foundInfo.username && (
                  <p className="text-sm text-gray-700 mb-1">
                    用户名：<span className="font-medium">{foundInfo.username}</span>
                  </p>
                )}
                {foundInfo.email && (
                  <p className="text-sm text-gray-700 mb-3">
                    邮箱：<span className="font-medium">{foundInfo.email}</span>
                  </p>
                )}
                <button
                  onClick={handleSendResetEmail}
                  className="w-full py-2 px-4 border border-red-600 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                >
                  发送密码重置邮件
                </button>
              </div>
            )}

            {resetUrl && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="text-sm font-medium text-blue-900 mb-2">密码重置链接：</h3>
                <p className="text-xs text-blue-700 mb-3 break-all">
                  {resetUrl}
                </p>
                <p className="text-xs text-blue-600 mb-3">
                  提示：生产环境中，此链接将通过邮件发送给您，而不会在此显示
                </p>
                <button
                  onClick={() => window.open(resetUrl, '_blank')}
                  className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  打开重置密码页面
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-red-600 hover:text-red-700"
            >
              返回登录
            </button>
          </div>

          <div className="text-center text-xs text-gray-500 mt-8">
            <p>如需帮助，请联系书院管理员</p>
          </div>
        </div>
      </div>
    </div>
  );
}
